import { createContext, useState, useEffect, useContext } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { LOCALES } from "../i18n/i18n";

export const UserPreferencesContext = createContext();

// Local declaration of group modes
const GROUP_MODES = {
	DAY: "day",
	CATEGORY: "category",
};

const getInitialValue = (key, fallback) =>
	localStorage.getItem(key) || fallback;

export const UserPreferencesProvider = ({ children }) => {
	const [themePreference, setThemePreference] = useState(() =>
		getInitialValue("themePreference", "system")
	);
	const [design, setDesign] = useState(() =>
		getInitialValue("design", "ios18")
	);
	const [locale, setLocale] = useState(() =>
		getInitialValue(
			"locale",
			navigator.language.startsWith("fr") ? LOCALES.FRENCH : LOCALES.ENGLISH
		)
	);
	const [groupMode, setGroupMode] = useState(() =>
		getInitialValue("groupMode", GROUP_MODES.CATEGORY)
	);
	const [bottomNavStyle, setBottomNavStyle] = useState(() =>
		getInitialValue("bottomNavStyle", "full")
	);

	const [authInitialized, setAuthInitialized] = useState(false);
	const [currentUser, setCurrentUser] = useState(null);
	const [userRole, setUserRole] = useState(null);
	const [systemTheme, setSystemTheme] = useState(() =>
		window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
	);

	// Listen to system theme changes
	useEffect(() => {
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

		const handleThemeChange = e => {
			const newSystemTheme = e.matches ? "dark" : "light";
			setSystemTheme(newSystemTheme);

			if (currentUser && themePreference === "system") {
				updateDoc(doc(db, "users", currentUser.uid), {
					"preferences.theme": newSystemTheme,
				}).catch(console.error);
			}
		};

		mediaQuery.addEventListener("change", handleThemeChange);
		return () => mediaQuery.removeEventListener("change", handleThemeChange);
	}, [currentUser, themePreference]);

	// Load user preferences on auth state change
	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async user => {
			setCurrentUser(user);

			if (user) {
				try {
					const userDocRef = doc(db, "users", user.uid);
					const userDocSnap = await getDoc(userDocRef);
					const userData = userDocSnap.data();
					const prefs = userData?.preferences || {};

					const useSystem = prefs.useSystemTheme === true;
					const storedTheme = prefs.theme || "light";

					setThemePreference(useSystem ? "system" : storedTheme);
					setLocale(prefs.language || locale);
					setGroupMode(prefs.groupMode || GROUP_MODES.CATEGORY);
					setBottomNavStyle(prefs.bottomNavStyle || "full");
					setDesign(prefs.design || "ios18");

					// Update localStorage
					localStorage.setItem(
						"themePreference",
						useSystem ? "system" : storedTheme
					);
					localStorage.setItem("locale", prefs.language || locale);
					localStorage.setItem(
						"groupMode",
						prefs.groupMode || GROUP_MODES.CATEGORY
					);
					localStorage.setItem(
						"bottomNavStyle",
						prefs.bottomNavStyle || "full"
					);
					localStorage.setItem("design", prefs.design || "ios18");

					// Sync Firestore if system theme differs
					if (useSystem && storedTheme !== systemTheme) {
						await updateDoc(userDocRef, {
							"preferences.theme": systemTheme,
						});
					}

					setUserRole(userData?.role || null);
				} catch (error) {
					console.error("Error getting user preferences:", error);
				}
			} else {
				// No user => reset from localStorage
				setThemePreference(getInitialValue("themePreference", "system"));
				setLocale(
					getInitialValue(
						"locale",
						navigator.language.startsWith("fr")
							? LOCALES.FRENCH
							: LOCALES.ENGLISH
					)
				);
				setGroupMode(getInitialValue("groupMode", GROUP_MODES.CATEGORY));
				setBottomNavStyle(getInitialValue("bottomNavStyle", "full"));
				setDesign(getInitialValue("design", "ios18"));
				setUserRole(null);
			}

			setAuthInitialized(true);
		});

		return () => unsubscribe();
	}, [locale, systemTheme]);

	// Sync state changes to Firestore and localStorage
	const syncPreference = (key, value, path) => {
		localStorage.setItem(key, value);
		if (currentUser) {
			updateDoc(doc(db, "users", currentUser.uid), {
				[path]: value,
			}).catch(console.error);
		}
	};

	useEffect(() => {
		const finalTheme =
			themePreference === "system" ? systemTheme : themePreference;
		localStorage.setItem("themePreference", themePreference);
		if (currentUser) {
			updateDoc(doc(db, "users", currentUser.uid), {
				"preferences.theme": finalTheme,
				"preferences.useSystemTheme": themePreference === "system",
			}).catch(console.error);
		}
	}, [themePreference, currentUser, systemTheme]);

	useEffect(() => {
		syncPreference("locale", locale, "preferences.language");
	}, [locale, currentUser]);

	useEffect(() => {
		syncPreference("groupMode", groupMode, "preferences.groupMode");
	}, [groupMode, currentUser]);

	useEffect(() => {
		syncPreference(
			"bottomNavStyle",
			bottomNavStyle,
			"preferences.bottomNavStyle"
		);
	}, [bottomNavStyle, currentUser]);

	useEffect(() => {
		syncPreference("design", design, "preferences.design");
	}, [design, currentUser]);

	return (
		<UserPreferencesContext.Provider
			value={{
				themePreference,
				setThemePreference,
				locale,
				setLocale,
				groupMode,
				setGroupMode,
				bottomNavStyle,
				setBottomNavStyle,
				authInitialized,
				currentUser,
				userRole,
				systemTheme,
				design,
				setDesign,
			}}
		>
			{children}
		</UserPreferencesContext.Provider>
	);
};

export const useUserPreferences = () => {
	const context = useContext(UserPreferencesContext);
	if (!context) {
		throw new Error(
			"useUserPreferences must be used within a UserPreferencesProvider"
		);
	}
	return context;
};
